import { NgClass } from '@angular/common'
import { HttpResponse } from '@angular/common/http'
import { Component, computed, DestroyRef, effect, ElementRef, input, OnDestroy, OnInit, output, signal, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { environment } from '@environments/environment'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { Subscription } from 'rxjs'
import { SafeDomPipe } from '../../pipes/safe-dom.pipe'
import { CropModalComponent } from '../crop-modal/crop-modal.component'
import { ModalComponent } from '../modal/modal.component'
import { ProgressCircleComponent } from '../progress-circle/progress-circle.component'
import { DeleteModalComponent } from '../upload/components/delete-modal/delete-modal.component'
import { UploadTypeEnum } from '../upload/enums/upload.enum'
import { IsImagePipe } from '../upload/pipes/is-image.pipe'
import { UploadService } from '../upload/services/upload.service'
import { FileResponse, UploadFile } from '../upload/types/upload.type'

@Component({
  selector: 'app-banner-upload-field',
  standalone: true,
  imports: [ReactiveFormsModule, NgClass, NgxTippyModule, DeleteModalComponent, NgxTippyModule, SafeDomPipe, ProgressCircleComponent, ModalComponent, CropModalComponent],
  providers: [UploadService, IsImagePipe],
  templateUrl: './banner-upload-field.component.html',
  styleUrl: './banner-upload-field.component.scss'
})
export class BannerUploadFieldComponent implements OnInit, OnDestroy {
  control = input.required<FormControl<string>>()
  label = input<string>('Upload your file')
  hint = input<string>('')
  showIcon = input<boolean>(true)
  minFileSize = input<number>(0) // KB
  isLoading = input<boolean>(false)
  readonly = input<boolean>(false)
  organizationId = input<number | null>(null)
  eventId = input<number | null>(null)
  originalImageUrl = input<string | null>(null)
  timelineLayout = input<boolean>(false)
  skipCrop = input<boolean>(false)
  cropModalHeader = input<string>('Event banner cropping')
  attachToEvent = input<boolean>(true)

  valueChanged = output<{ field: string; value: string; original_image?: string }>()

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>
  public isFileDraggedOver = signal<boolean>(false)
  public isUploadFieldPressed = signal<boolean>(false)
  public warningMessage = signal<string>('')
  public fileErrorMessage = signal<string>('')
  public isRequired = signal<boolean>(false)
  public deleteModalId: string = crypto.randomUUID()
  public actionsModalId: string = crypto.randomUUID()
  public cropModalId: string = crypto.randomUUID()
  private _isUploading = signal<boolean>(false)
  public uploadProgress = signal<number>(0)
  public controlValue = signal<string>('')
  public pendingBannerValue = signal<string | null>(null)
  public loadedBannerValue = signal<string | null>(null)
  public deletingBanner = signal<boolean>(false)
  public originalImageFile = signal<File | string | null>(null)
  public cropImageFile = signal<File | string | null>(null)
  public savedCropBox = signal<{ x: number; y: number; width: number; height: number } | null>(null)
  private _controlValueSubscription?: Subscription
  private _uploadSubscription?: Subscription
  private readonly ORIGINAL_MAX_WIDTH = 2000
  private readonly ORIGINAL_MAX_HEIGHT = 2000
  private _lastLoggedMismatchSignature: string | null = null

  public changeIcon: string = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<path d="M16.3208 10.5767C15.7909 10.5767 15.3607 11.0148 15.3607 11.5545V13.51H3.27665L4.51808 12.2458C4.89349 11.8635 4.89349 11.2455 4.51808 10.8632C4.14268 10.4809 3.53588 10.4809 3.16048 10.8632L0.280115 13.7965C0.191784 13.8864 0.121695 13.995 0.0727291 14.1143C-0.024243 14.3538 -0.024243 14.6227 0.0727291 14.8613C0.121695 14.9806 0.191784 15.0891 0.280115 15.1791L3.16048 18.1123C3.3477 18.303 3.59349 18.3988 3.83928 18.3988C4.08507 18.3988 4.33086 18.303 4.51808 18.1123C4.89349 17.73 4.89349 17.1121 4.51808 16.7298L3.27665 15.4655H16.3208C16.8508 15.4655 17.281 15.0275 17.281 14.4878V11.5545C17.281 11.0148 16.8508 10.5767 16.3208 10.5767Z" fill="currentColor"/>
<path d="M19.1273 4.33662C19.0783 4.21734 19.0082 4.1088 18.9199 4.01885L16.0395 1.08556C15.6641 0.703252 15.0573 0.703252 14.6819 1.08556C14.3065 1.46786 14.3065 2.08581 14.6819 2.46812L15.9234 3.73237H2.87916C2.34917 3.73237 1.91904 4.1704 1.91904 4.71013V7.64342C1.91904 8.18315 2.34917 8.62119 2.87916 8.62119C3.40915 8.62119 3.83928 8.18315 3.83928 7.64342V5.68789H15.9234L14.6819 6.95214C14.3065 7.33445 14.3065 7.9524 14.6819 8.3347C14.8691 8.52537 15.1149 8.62119 15.3607 8.62119C15.6065 8.62119 15.8523 8.52537 16.0395 8.3347L18.9199 5.40141C19.0082 5.31145 19.0783 5.20292 19.1273 5.08364C19.2242 4.84506 19.2242 4.5752 19.1273 4.33662Z" fill="currentColor"/>
</svg>
`
  public cropIcon: string = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<path d="M2.9184 0H3.456V0.0384H3.5712V0.0768H3.6864V0.1152H3.7248V0.1536H3.8016V0.192H3.84V0.2688H3.8784V0.3072H3.9168V0.384H3.9552V0.4608H3.9936V0.576H4.032V2.3808H14.8224V2.4192H15.0144V2.4576H15.168V2.496H15.2832V2.5344H15.36V2.5728H15.4368V2.6112H15.5136V2.6496H15.5904V2.688H15.6672V2.7264H15.744V2.7648H15.7824V2.8032H15.8592V2.8416H15.8976V2.9184H15.9744V2.9568H16.0128V2.9952H16.0512V3.0336H16.0896V3.072H16.128V3.1104H16.1664V3.1488H16.2048V3.1872H16.2432V3.2256H16.2816V3.3024H16.3584V3.3408H16.3968V3.4176H16.4352V3.456H16.4736V3.5328H16.512V3.6096H16.5504V3.6864H16.5888V3.7632H16.6272V3.84H16.6656V3.9168H16.704V4.032H16.7424V4.1856H16.7808V4.3392H16.8192V15.168H18.624V15.2064H18.7392V15.2448H18.816V15.2832H18.8928V15.3216H18.9312V15.36H18.9696V15.3984H19.008V15.4368H19.0464V15.4752H19.0848V15.5136H19.1232V15.6288H19.1616V15.744H19.2V16.2432H19.1616V16.3584H19.1232V16.4736H19.0848V16.5504H19.0464V16.5888H19.008V16.6272H18.9696V16.6656H18.8928V16.704H18.8544V16.7424H18.7776V16.7808H18.6624V16.8192H16.8192V18.6624H16.7808V18.7776H16.7424V18.8544H16.704V18.8928H16.6656V18.9696H16.6272V19.008H16.5888V19.0464H16.5504V19.0848H16.4736V19.1232H16.3584V19.1616H16.2432V19.2H15.744V19.1616H15.6288V19.1232H15.5136V19.0848H15.4752V19.0464H15.4368V19.008H15.3984V18.9696H15.36V18.9312H15.3216V18.8928H15.2832V18.816H15.2448V18.7392H15.2064V18.624H15.168V16.8192H4.3776V16.7808H4.1856V16.7424H4.032V16.704H3.9168V16.6656H3.84V16.6272H3.7632V16.5888H3.6864V16.5504H3.6096V16.512H3.5328V16.4736H3.456V16.4352H3.4176V16.3968H3.3792V16.3584H3.3024V16.2816H3.2256V16.2432H3.1872V16.2048H3.1488V16.1664H3.1104V16.128H3.072V16.0896H3.0336V16.0512H2.9952V16.0128H2.9568V15.9744H2.9184V15.8976H2.8416V15.8208H2.8032V15.7824H2.7648V15.7056H2.7264V15.6672H2.688V15.5904H2.6496V15.5136H2.6112V15.4368H2.5728V15.36H2.5344V15.2832H2.496V15.168H2.4576V15.0144H2.4192V14.8224H2.3808V4.032H0.576V3.9936H0.4608V3.9552H0.384V3.9168H0.3072V3.8784H0.2688V3.84H0.2304V3.8016H0.192V3.7632H0.1536V3.7248H0.1152V3.6864H0.0768V3.5712H0.0384V3.456H0V2.9184H0.0384V2.8416H0.0768V2.7264H0.1152V2.6496H0.1536V2.6112H0.192V2.5728H0.2304V2.5344H0.2688V2.496H0.3456V2.4576H0.4224V2.4192H0.5376V2.3808H2.3808V0.5376H2.4192V0.4224H2.4576V0.3456H2.496V0.3072H2.5344V0.2304H2.5728V0.192H2.6112V0.1536H2.6496V0.1152H2.688V0.0768H2.8416V0.0384H2.9184V0ZM4.032 4.032V11.8272H4.0704V11.7888H4.1088V11.7504H4.1472V11.712H4.1856V11.6736H4.224V11.6352H4.2624V11.5968H4.3008V11.5584H4.3392V11.52H4.3776V11.4816H4.416V11.4432H4.4544V11.4048H4.4928V11.3664H4.5312V11.328H4.5696V11.2896H4.6464V11.2512H4.6848V11.2128H4.7616V11.1744H4.8V11.136H4.8768V11.0976H4.9152V11.0592H4.992V11.0208H5.0688V10.9824H5.2224V10.944H5.5296V10.9056H5.6832V10.944H5.9904V10.9824H6.1056V11.0208H6.2208V11.0592H6.2976V11.0976H6.336V11.136H6.4128V11.1744H6.4512V11.2128H6.4896V11.2512H6.5664V11.2896H6.6432V11.328H6.6816V11.3664H6.72V11.4048H6.7584V11.4432H6.7968V11.4816H6.8352V11.52H6.8736V11.5584H6.912V11.5968H6.9504V11.6352H6.9888V11.6736H7.0272V11.712H7.0656V11.7504H7.104V11.7888H7.1424V11.8272H7.1808V11.8656H7.2192V11.904H7.2576V11.9424H7.296V11.9808H7.3344V12.0192H7.3728V12.0576H7.4112V12.096H7.4496V12.1344H7.488V12.1728H7.5264V12.2112H7.5648V12.2496H7.6032V12.288H7.6416V12.2496H7.68V12.2112H7.7184V12.1728H7.7568V12.1344H7.7952V12.096H7.8336V12.0576H7.872V12.0192H7.9104V11.9808H7.9488V11.9424H7.9872V11.904H8.0256V11.8656H8.064V11.7888H8.1024V11.7504H8.1408V11.712H8.2176V11.6736H8.256V11.6352H8.2944V11.5584H8.3328V11.52H8.3712V11.4816H8.4096V11.4432H8.448V11.4048H8.4864V11.3664H8.5248V11.328H8.5632V11.2896H8.64V11.2512H8.6784V11.2128H8.7168V11.1744H8.7552V11.136H8.7936V11.0976H8.832V11.0592H8.8704V11.0208H8.9088V10.9824H8.9472V10.944H8.9856V10.9056H9.024V10.8672H9.0624V10.8288H9.1008V10.7904H9.1392V10.752H9.1776V10.7136H9.216V10.6752H9.2544V10.6368H9.2928V10.5984H9.3312V10.56H9.3696V10.5216H9.408V10.4832H9.4464V10.4448H9.4848V10.4064H9.5232V10.368H9.5616V10.3296H9.6V10.2912H9.6384V10.2528H9.6768V10.176H9.7152V10.1376H9.7536V10.0992H9.8304V10.0608H9.8688V9.984H9.9072V9.9456H9.9456V9.9072H9.984V9.8688H10.0224V9.8304H10.0608V9.792H10.0992V9.7536H10.1376V9.7152H10.176V9.6768H10.2528V9.6384H10.2912V9.6H10.3296V9.5616H10.368V9.5232H10.4064V9.4848H10.4448V9.4464H10.4832V9.408H10.5216V9.3696H10.56V9.3312H10.5984V9.2928H10.6368V9.2544H10.6752V9.216H10.7136V9.1776H10.752V9.1392H10.7904V9.1008H10.8288V9.0624H10.8672V9.024H10.9056V8.9856H10.944V8.9472H10.9824V8.9088H11.0208V8.8704H11.0592V8.832H11.0976V8.7936H11.136V8.7552H11.1744V8.7168H11.2128V8.6784H11.2512V8.64H11.2896V8.5632H11.328V8.5248H11.3664V8.4864H11.4432V8.448H11.4816V8.3712H11.52V8.3328H11.5968V8.2944H11.6736V8.256H11.7504V8.2176H11.8272V8.1792H11.9424V8.1408H12.096V8.1024H12.7104V8.1408H12.864V8.1792H12.9792V8.2176H13.056V8.256H13.1328V8.2944H13.1712V8.3328H13.248V8.3712H13.2864V8.4096H13.3248V8.448H13.4016V8.4864H13.44V8.5248H13.4784V8.5632H13.5168V8.6016H13.5552V8.64H13.5936V8.6784H13.632V8.7168H13.6704V8.7552H13.7088V8.7936H13.7472V8.832H13.7856V8.8704H13.824V8.9088H13.8624V8.9472H13.9008V8.9856H13.9392V9.024H13.9776V9.0624H14.016V9.1008H14.0544V9.1392H14.0928V9.1776H14.1312V9.216H14.1696V9.2544H14.208V9.2928H14.2464V9.3312H14.2848V9.3696H14.3232V9.4464H14.3616V9.4848H14.4V9.5232H14.4384V9.5616H14.4768V9.6H14.5152V9.6384H14.5536V9.6768H14.6304V9.7152H14.6688V9.7536H14.7072V9.792H14.7456V9.8304H14.784V9.8688H14.8224V9.9072H14.8608V9.9456H14.8992V9.984H14.9376V10.0224H14.976V10.0608H15.0144V10.0992H15.0528V10.1376H15.0912V10.176H15.1296V10.2144H15.168V4.6848H15.1296V4.5696H15.0912V4.4928H15.0528V4.416H15.0144V4.3392H14.976V4.3008H14.9376V4.2624H14.8992V4.224H14.8608V4.1856H14.784V4.1472H14.7456V4.1088H14.6304V4.0704H14.5152V4.032H4.032Z" fill="currentColor"/>
<path d="M6.87372 5.56787H7.48812V5.60627H7.68012V5.64467H7.79532V5.68307H7.87212V5.72147H7.94892V5.75987H8.02572V5.79827H8.06412V5.83667H8.14092V5.87507H8.17932V5.91347H8.21772V5.95187H8.29452V5.99027H8.33292V6.02867H8.37132V6.06707H8.40972V6.10547H8.44812V6.18227H8.48652V6.22067H8.52492V6.25907H8.56332V6.33587H8.60172V6.37427H8.64012V6.45107H8.67852V6.48947H8.71692V6.60467H8.75532V6.71987H8.79372V6.87347H8.83212V7.48787H8.79372V7.67987H8.75532V7.79507H8.71692V7.91027H8.67852V7.94867H8.64012V8.02547H8.60172V8.06387H8.56332V8.14067H8.52492V8.17907H8.48652V8.21747H8.44812V8.29427H8.40972V8.33267H8.37132V8.37107H8.33292V8.40947H8.29452V8.44787H8.21772V8.48627H8.17932V8.52467H8.14092V8.56307H8.06412V8.60147H8.02572V8.63987H7.94892V8.67827H7.87212V8.71667H7.79532V8.75507H7.68012V8.79347H7.48812V8.83187H6.91212V8.79347H6.72012V8.75507H6.60492V8.71667H6.52812V8.67827H6.45132V8.63987H6.37452V8.60147H6.33612V8.56307H6.25932V8.52467H6.22092V8.48627H6.18252V8.44787H6.10572V8.40947H6.06732V8.37107H6.02892V8.33267H5.99052V8.29427H5.95212V8.21747H5.91372V8.17907H5.87532V8.14067H5.83692V8.06387H5.79852V8.02547H5.76012V7.94867H5.72172V7.87187H5.68332V7.79507H5.64492V7.67987H5.60652V7.48787H5.56812V6.87347H5.60652V6.71987H5.64492V6.60467H5.68332V6.48947H5.72172V6.45107H5.76012V6.37427H5.79852V6.33587H5.83692V6.25907H5.87532V6.22067H5.91372V6.18227H5.95212V6.10547H5.99052V6.06707H6.02892V6.02867H6.06732V5.99027H6.10572V5.95187H6.18252V5.91347H6.22092V5.87507H6.25932V5.83667H6.33612V5.79827H6.37452V5.75987H6.45132V5.72147H6.52812V5.68307H6.60492V5.64467H6.72012V5.60627H6.87372V5.56787Z" fill="currentColor"/>
</svg>
`

  public deleteIcon: string = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<path d="M17.4223 4.04211H13.5112V2.02105C13.5112 1.48504 13.3051 0.970973 12.9384 0.591953C12.5717 0.212932 12.0743 0 11.5556 0H7.64449C7.12585 0 6.62844 0.212932 6.26171 0.591953C5.89497 0.970973 5.68894 1.48504 5.68894 2.02105V4.04211H1.77783C1.5185 4.04211 1.2698 4.14857 1.08643 4.33808C0.903064 4.52759 0.800049 4.78462 0.800049 5.05263C0.800049 5.32064 0.903064 5.57767 1.08643 5.76718C1.2698 5.95669 1.5185 6.06316 1.77783 6.06316H2.7556V17.1789C2.7556 17.715 2.96164 18.229 3.32837 18.608C3.69511 18.9871 4.19251 19.2 4.71116 19.2H14.4889C15.0076 19.2 15.505 18.9871 15.8717 18.608C16.2385 18.229 16.4445 17.715 16.4445 17.1789V6.06316H17.4223C17.6816 6.06316 17.9303 5.95669 18.1137 5.76718C18.297 5.57767 18.4 5.32064 18.4 5.05263C18.4 4.78462 18.297 4.52759 18.1137 4.33808C17.9303 4.14857 17.6816 4.04211 17.4223 4.04211ZM7.64449 2.02105H11.5556V4.04211H7.64449V2.02105ZM8.62227 15.1579C8.62227 15.4259 8.51926 15.6829 8.33589 15.8724C8.15252 16.062 7.90382 16.1684 7.64449 16.1684C7.38517 16.1684 7.13647 16.062 6.9531 15.8724C6.76973 15.6829 6.66672 15.4259 6.66672 15.1579V8.08421C6.66672 7.8162 6.76973 7.55917 6.9531 7.36966C7.13647 7.18015 7.38517 7.07368 7.64449 7.07368C7.90382 7.07368 8.15252 7.18015 8.33589 7.36966C8.51926 7.55917 8.62227 7.8162 8.62227 8.08421V15.1579ZM12.5334 15.1579C12.5334 15.4259 12.4304 15.6829 12.247 15.8724C12.0636 16.062 11.8149 16.1684 11.5556 16.1684C11.2963 16.1684 11.0476 16.062 10.8642 15.8724C10.6808 15.6829 10.5778 15.4259 10.5778 15.1579V8.08421C10.5778 7.8162 10.6808 7.55917 10.8642 7.36966C11.0476 7.18015 11.2963 7.07368 11.5556 7.07368C11.8149 7.07368 12.0636 7.18015 12.247 7.36966C12.4304 7.55917 12.5334 7.8162 12.5334 8.08421V15.1579Z" fill="#E02424"/>
</svg>
`

  private readonly _ALLOWED_FORMATS = ['png', 'jpg', 'jpeg', 'webp', 'bmp']

  // Effect to sync control value to signal when control changes
  constructor(private _ModalService: ModalService, private _UploadService: UploadService, private _alertService: AlertService, private _destroyRef: DestroyRef) {
    effect(() => {
      const ctrl = this.control()
      // Cleanup previous subscription
      if (this._controlValueSubscription) {
        this._controlValueSubscription.unsubscribe()
      }
      if (ctrl) {
        // Store initial value
        const initialValue = ctrl.value || ''
        this.controlValue.set(initialValue)
        if (initialValue && !this.originalImageFile()) this.originalImageFile.set(initialValue)

        // Subscribe to valueChanges
        this._controlValueSubscription = ctrl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
          // If we have a pending banner value, ignore external updates to prevent showing old banner
          if (this.pendingBannerValue()) return

          const nextValue = String(value ?? '').trim()
          if (this.deletingBanner() && nextValue) return
          if (this.deletingBanner() && !nextValue) this.deletingBanner.set(false)

          const loadedValue = String(this.loadedBannerValue() ?? '').trim()
          // While we have a locally loaded banner, ignore stale external writes until parent catches up.
          if (loadedValue && nextValue !== loadedValue) return

          // Normal update - control value changed externally
          this.controlValue.set(value || '')
          // Parent caught up with the locally loaded value; release optimistic override.
          if (loadedValue && nextValue === loadedValue) this.loadedBannerValue.set(null)
        })
      }

      // Debug only: detect unexpected long-lived mismatches between displayed src and control value.
      const path = this.pathValue()
      const controlValue = (this.control()?.value || '').trim()
      const pending = this.pendingBannerValue()
      const isUploading = this._isUploading()
      const hasMismatch = !pending && !isUploading && path.trim() !== controlValue
      if (!hasMismatch) {
        this._lastLoggedMismatchSignature = null
        return
      }
      const signature = `${path}::${controlValue}`
      if (signature === this._lastLoggedMismatchSignature) return
      this._lastLoggedMismatchSignature = signature
    })
  }

  ngOnInit(): void {
    if (this.control()) this.isRequired.set(this.control().hasValidator(Validators.required))
  }

  /**
   * Open actions modal (mobile view)
   */
  public openActionsModal(): void {
    if (this.readonly()) return
    this.toggleModal(this.actionsModalId)
  }

  /**
   * Handle action button click
   * @param action {string} - The action to perform ('change', 'crop', 'delete')
   */
  public onActionClicked(action: string): void {
    this.toggleModal(this.actionsModalId)
    switch (action) {
      case 'change':
        this.changeBanner()
        break
      case 'delete':
        this.deleteFile()
        break
      case 'crop':
        this.openCropModal()
        break
    }
  }

  ngOnDestroy(): void {
    if (this._uploadSubscription) this._uploadSubscription.unsubscribe()
    if (this._controlValueSubscription) this._controlValueSubscription.unsubscribe()
  }

  /**
   * Handle file selection and validation
   * @param files {FileList | File[]} - FileList or File array
   */
  private _handleFileSelection(files: FileList | File[]): void {
    const fileArray = Array.from(files)
    if (!fileArray.length) return

    const file = fileArray[0]
    this.warningMessage.set('')
    this.fileErrorMessage.set('')

    const formatError = this._validateFileFormatFromName(file.name)
    if (formatError) {
      this._setFileError(formatError)
      return
    }
    if (!this._isImageFile(file)) {
      this._setFileError('Unsupported file format. Please upload PNG, JPG, JPEG, WEBP or BMP.')
      return
    }

    if (this.skipCrop()) {
      this.originalImageFile.set(file)
      this.cropImageFile.set(null)
      this.savedCropBox.set(null)
      this._uploadFile(file)
      return
    }

    // Store original file and show crop modal
    this.originalImageFile.set(file)
    this.cropImageFile.set(file)
    // Clear any previously saved crop box when uploading new image
    this.savedCropBox.set(null)
    this.openCropModal()
  }

  /**
   * Set file error message and show alert when user already has a banner (error state not visible)
   * @param message {string} - Error message to set
   */
  private _setFileError(message: string): void {
    this.fileErrorMessage.set(message)
    if (this.hasFile()) this._alertService.alert(Alert.ERROR, message)
  }

  /**
   * Check if file is an image by MIME type (guards against extension override)
   * @param file {File} - File to check
   */
  private _isImageFile(file: File): boolean {
    const type = (file.type || '').toLowerCase()
    if (!type) return false
    return type.startsWith('image/')
  }

  /**
   * Open crop modal.
   * When opening for an existing banner (re-crop), use originalImageUrl so the cropper shows the original image.
   * When opening after the user just selected a new file (Change/upload), do not overwrite cropImageFile – it was already set to the new File.
   */
  public openCropModal(): void {
    if (this.skipCrop()) return
    const alreadyHaveNewFile = this.cropImageFile() instanceof File
    if (this.hasFile() && !alreadyHaveNewFile) {
      if (this.originalImageUrl()) this.cropImageFile.set(this.originalImageUrl()!)
      else if (this.originalImageFile()) this.cropImageFile.set(this.originalImageFile()!)
      else if (this.pathValue()) this.cropImageFile.set(this.pathValue()!)
    }
    this.toggleModal(this.cropModalId)
  }

  /**
   * Handle cropped image – always upload file=original (optionally resized via canvas) and cropped=cropped blob.
   */
  public onImageCropped(result: { blob: Blob; cropBox: { x: number; y: number; width: number; height: number } }): void {
    this.savedCropBox.set(result.cropBox)
    const original = this.originalImageFile()
    const croppedFile = new File([result.blob], original instanceof File ? original.name : 'cropped-image.png', {
      type: result.blob.type || 'image/png'
    })

    const originalSource: File | string | null = original instanceof File ? original : this.originalImageUrl() ?? (typeof original === 'string' ? original : null)

    if (!originalSource) {
      // No known original – fall back to uploading only the cropped image
      this._uploadFile(croppedFile)
      return
    }

    const originalName = original instanceof File ? original.name : this._extractNameFromUrl(typeof originalSource === 'string' ? originalSource : '')
    const originalType = original instanceof File ? original.type : result.blob.type || 'image/png'

    this._resizeImageToMax(originalSource, this.ORIGINAL_MAX_WIDTH, this.ORIGINAL_MAX_HEIGHT, originalName, originalType)
      .then((resizedOriginal) => this._uploadFile(resizedOriginal, croppedFile))
      .catch(() => {
        // If resize fails, still try to upload something sensible
        if (original instanceof File) this._uploadFile(original, croppedFile)
        else this._uploadFile(croppedFile)
      })
  }

  /**
   * Resize image to maximum width and height
   * @param source {File | string} - The source file or URL
   * @param maxWidth {number} - The maximum width
   * @param maxHeight {number} - The maximum height
   * @param name {string} - The name of the image
   * @param type {string} - The type of the image
   * @returns {Promise<File>} - The resized image
   */
  private _resizeImageToMax(source: File | string, maxWidth: number, maxHeight: number, name: string, type: string): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image()

      if (typeof source === 'string') {
        const url = source.startsWith('http') ? source : environment.s3_files_root + source
        try {
          const urlOrigin = new URL(url, window.location.href).origin
          if (urlOrigin !== window.location.origin) img.crossOrigin = 'anonymous'
        } catch {
          img.crossOrigin = 'anonymous'
        }
        img.src = url
      } else {
        const reader = new FileReader()
        reader.onload = (e) => {
          if (!e.target?.result) {
            reject(new Error('Failed to read original image'))
            return
          }
          img.src = e.target.result as string
        }
        reader.onerror = () => reject(new Error('Failed to read original image'))
        reader.readAsDataURL(source)
      }

      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img

        const widthRatio = maxWidth > 0 ? maxWidth / width : 1
        const heightRatio = maxHeight > 0 ? maxHeight / height : 1
        const ratio = Math.min(widthRatio, heightRatio, 1) // never upscale

        width = Math.round(width * ratio)
        height = Math.round(height * ratio)

        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to create resized blob'))
            return
          }
          resolve(new File([blob], name || 'original-image.png', { type: type || blob.type }))
        }, type || 'image/png')
      }

      img.onerror = () => reject(new Error('Failed to load original image'))
    })
  }

  /**
   * Extract name from URL
   * @param url {string} - The URL to extract the name from
   * @returns {string} - The name of the image
   */
  private _extractNameFromUrl(url: string): string {
    if (!url) return 'original-image.png'
    try {
      const u = url.startsWith('http') ? new URL(url) : new URL(environment.s3_files_root + url)
      const parts = u.pathname.split('/')
      const last = parts.pop() || 'original-image.png'
      return last.split('?')[0] || 'original-image.png'
    } catch {
      const parts = url.split('/')
      const last = parts.pop() || 'original-image.png'
      return last.split('?')[0] || 'original-image.png'
    }
  }

  /**
   * Handle crop cancellation
   */
  public onCropCancelled(): void {
    // Revert transient "change image" selection so reopening crop uses the persisted banner.
    const hasTransientSelection = this.cropImageFile() instanceof File || this.originalImageFile() instanceof File
    if (!hasTransientSelection) return

    const persistedSource = this.originalImageUrl() || this.pathValue() || null
    this.originalImageFile.set(persistedSource)
    this.cropImageFile.set(persistedSource)
  }

  /**
   * Handle image load failure in cropper (e.g. invalid file type like PDF) – show error state
   */
  public onCropLoadFailed(): void {
    this.warningMessage.set('')
    const message = 'Failed to load image. Please upload a valid image (PNG, JPG, JPEG, WEBP or BMP).'
    this._setFileError(message)
    this.control().markAsTouched()
    if (!this.hasFile()) {
      this.originalImageFile.set(null)
      this.cropImageFile.set(null)
    }
  }

  /**
   * Upload file using UploadService.
   * Sends file (original image) and optionally cropped (cropped image).
   * @param file {File} - Original image file
   * @param cropped {File} - Optional cropped image file
   */
  private _uploadFile(file: File, cropped?: File): void {
    if (this._isUploading()) return

    this._isUploading.set(true)
    this.uploadProgress.set(0)
    this.warningMessage.set('')
    this.fileErrorMessage.set('')

    // Cancel any existing upload
    if (this._uploadSubscription) {
      this._uploadSubscription.unsubscribe()
    }

    const payload: UploadFile = this.attachToEvent()
      ? { file, name: file.name, organization_id: this.organizationId() ?? undefined, event_id: this.eventId(), type: UploadTypeEnum.EVENT }
      : { file, name: file.name, organization_id: this.organizationId() ?? undefined , type: UploadTypeEnum.EMAIL_TEMPLATE}
    if (cropped) payload.cropped = cropped

    this._uploadSubscription = this._UploadService
      .uploadFile(payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) => {
          if (typeof result === 'number') {
            // Upload progress
            this.uploadProgress.set(result)
          } else if (result && 'data' in result) {
            // Upload completed - get file URL
            const uploadResponse = result as Response<{ uploads: FileResponse }>
            const uploads = uploadResponse.data?.uploads
            if (uploads?.name) {
              // Forward upload original_url into valueChanged.original_image only; consumers that ignore it are unchanged.
              const originalUrl = uploads.original_url?.trim() || null
              if (uploads.path_url) this._setBannerValue(uploads.path_url, originalUrl)
              else if (uploads.path) this._setBannerValue(uploads.path, originalUrl)
              else this._getFileUrl(uploads.name, originalUrl)
            } else {
              this._isUploading.set(false)
              this.uploadProgress.set(0)
              this.warningMessage.set('Upload completed but file URL could not be retrieved.')
              this.control().markAsTouched()
            }
          }
        },
        error: (error) => {
          this._isUploading.set(false)
          this.uploadProgress.set(0)
          this.warningMessage.set(error?.error?.message || 'An error occurred during upload. Please try again.')
          this.control().markAsTouched()
        }
      })
  }

  /**
   * Get file URL after upload
   * @param fileName {string} - Name of the uploaded file
   * @param originalUrl {string | null} - Optional pre-crop original URL from the upload response
   */
  private _getFileUrl(fileName: string, originalUrl: string | null = null): void {
    const ctrl = this.control()
    this._UploadService
      .getFile(fileName)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          const fileUrl = response.url || fileName
          this._setBannerValue(fileUrl, originalUrl)
        },
        error: () => {
          this._isUploading.set(false)
          this.uploadProgress.set(0)
          this.warningMessage.set('File uploaded but URL could not be retrieved.')
          ctrl?.markAsTouched()
        }
      })
  }

  /**
   * Set banner value and preload image before clearing loading state
   * @param filePath {string} - The cropped/display file path/URL
   * @param originalUrlFromUpload {string | null} - Optional pre-crop original URL from the upload response
   */
  private _setBannerValue(filePath: string, originalUrlFromUpload: string | null = null): void {
    const ctrl = this.control()
    this.pendingBannerValue.set(filePath)
    const preloadUrl = this._resolveFullBannerUrl(filePath)

    // Clear loaded value so we keep showing the old banner (via controlValue)
    this.loadedBannerValue.set(null)
    this.warningMessage.set('')

    const originalImage =
      (originalUrlFromUpload || '').trim() || (this.originalImageUrl() || '').trim() || undefined

    const payload: { field: string; value: string; original_image?: string } = { field: 'cover_page', value: filePath }
    if (originalImage) payload.original_image = originalImage
    this.valueChanged.emit(payload)

    if (originalUrlFromUpload) this.originalImageFile.set(originalUrlFromUpload)
    else if (!(this.originalImageFile() instanceof File)) this.originalImageFile.set(filePath)

    const img = new Image()
    img.onload = () => {
      this.loadedBannerValue.set(filePath)
      ctrl?.setValue(filePath, { emitEvent: false })
      ctrl?.markAsDirty()
      ctrl?.markAsTouched()
      this.controlValue.set(filePath)
      this.pendingBannerValue.set(null)
      this._isUploading.set(false)
      this.uploadProgress.set(0)
    }
    img.onerror = () => {
      this.pendingBannerValue.set(null)
      this.loadedBannerValue.set(null)
      this._isUploading.set(false)
      this.uploadProgress.set(0)
      this._setFileError('Failed to load image.')
    }
    img.src = preloadUrl
  }

  /**
   * Handle drag & drop or file input change
   * @param {DragEvent | Event} event - The event object
   */
  public onFileChange(event: DragEvent | Event): void {
    if (this.readonly()) return
    event.preventDefault()
    event.stopPropagation()
    this.isFileDraggedOver.set(false)

    const files = (event as DragEvent).dataTransfer?.files || (event.target as HTMLInputElement).files
    if (files) this._handleFileSelection(files)

    // Reset file input
    if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = ''
  }

  /**
   * Validate file format from filename
   */
  private _validateFileFormatFromName(fileName: string): string {
    const fileExtension = fileName.split('.').pop()?.toLowerCase()
    if (!fileExtension || !this._ALLOWED_FORMATS.includes(fileExtension)) {
      return 'Unsupported file format. Please upload PNG, JPG, JPEG, WEBP or BMP'
    }
    return ''
  }

  /**
   * Open file picker
   */
  public openFilePicker(): void {
    if (this.readonly()) return
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.click()
    }
  }

  /**
   * Mark upload field as pressed/focused for visual feedback.
   */
  public onUploadFieldPressStart(): void {
    this.isUploadFieldPressed.set(true)
  }

  /**
   * Clear upload field pressed/focused visual feedback.
   */
  public onUploadFieldPressEnd(): void {
    this.isUploadFieldPressed.set(false)
  }

  /**
   * Handle drag over event
   * @param event {Event} - The event object
   */
  public fileDragOver(event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    this.isFileDraggedOver.set(true)
  }

  /**
   * Handle drag leave event
   * @param event {Event} - The event object
   */
  public fileDragLeave(event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    this.isFileDraggedOver.set(false)
  }

  /**
   * Get current path value
   * @returns {string} - The current path value
   */
  public pathValue = computed<string>(() => {
    if (this.deletingBanner()) return ''
    // Prefer pending/locally-loaded banner to avoid stale parent overwrites after successful upload.
    return this.pendingBannerValue() || this.loadedBannerValue() || String(this.control()?.value ?? '').trim() || this.controlValue() || ''
  })

  /**
   * Check if file is uploaded
   * @returns {boolean} - True if file is uploaded, false otherwise
   */
  public hasFile = computed<boolean>(() => {
    return (this.pathValue() || '').trim().length > 0
  })

  /**
   * Empty upload area while in timeline layout (no file, not loading).
   */
  public isTimelineEmptyUpload = computed<boolean>(() => {
    return this.timelineLayout() && !this.isLoading() && !this.hasFile()
  })

  /**
   * Check if we should show the loading overlay
   * @returns {boolean} - True if uploading or has pending banner value
   */
  public showLoadingOverlay = computed<boolean>(() => {
    return this._isUploading() || !!this.pendingBannerValue()
  })

  /**
   * One full-width loading skeleton for all cases (parent PATCH, upload, image preload).
   * Keeps a single gray block instead of stacking `isLoading` + `showLoadingOverlay` + nested overlays.
   */
  /** Keep showing an existing banner while PATCH save runs; only skeleton when no image yet. */
  public showUnifiedBannerLoading = computed<boolean>(() => (this.isLoading() && !this.hasFile()) || this.showLoadingOverlay())

  /** Desktop hover toolbar: only when a banner path exists and not in upload/pending overlay. */
  public showDesktopBannerOverlay = computed<boolean>(() => this.hasFile() && !this.showLoadingOverlay())

  /**
   * Check if control is invalid and touched
   * @returns {boolean} - True if control is invalid and touched, false otherwise
   */
  public isInvalid = computed<boolean>(() => {
    const ctrl = this.control()
    return ctrl?.invalid && ctrl?.touched
  })

  /**
   * File / validation messages only (no hint). Hint is shown separately so it can be hidden on small screens.
   */
  public bannerProblemMessage = computed<string>(() => {
    if (this.fileErrorMessage()) return this.fileErrorMessage()
    if (this.warningMessage()) return this.warningMessage()
    const ctrl = this.control()
    if (this.isInvalid() && ctrl?.errors?.['required']) return 'This field is required'
    return ''
  })

  /**
   * Problem text if any, otherwise the hint (for single-line consumers).
   */
  public errorMessage = computed<string>(() => {
    const problem = this.bannerProblemMessage()
    if (problem) return problem
    return this.hint()
  })

  /**
   * Whether to show error state (red) – file error or control invalid
   */
  public showErrorState = computed<boolean>(() => {
    return !!this.fileErrorMessage() || this.isInvalid()
  })

  /**
   * Change banner - opens file picker to change the uploaded banner
   * @returns {void}
   */
  public changeBanner(): void {
    this.openFilePicker()
  }

  /**
   * Get image source for cropper (File or full URL).
   */
  public getCropImageSource(): File | string | null {
    const source = this.cropImageFile()
    if (!source) return null
    if (source instanceof File || source.startsWith('http')) return source

    const originalPath = this.originalImageUrl()
    if (originalPath) return this._resolveFullBannerUrl(originalPath)

    return this._resolveFullBannerUrl(source)
  }

  /**
   * After LQIP loads, fetch the display-sized image in the background.
   */
  public onBannerImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement | null
    if (!img) return

    const bannerPath = this.pathValue()
    const fullRes = this.getDisplayBannerSrc()
    const lqip = this.getDisplayBannerLqipUrl()
    if (fullRes === lqip || img.getAttribute('data-banner-upgraded') === bannerPath) return

    const hiRes = new Image()
    hiRes.onload = () => {
      if (this.pathValue() !== bannerPath) return
      img.setAttribute('data-banner-upgraded', bannerPath)
      img.src = fullRes
    }
    hiRes.src = fullRes
  }

  /**
   * Resolve banner image src for UI display using the same normalization used by cropper.
   */
  public getDisplayBannerSrc(): string {
    return this._resolveFullBannerUrl(this.pathValue())
  }

  /**
   * LQIP URL via uploads API (fast placeholder for micetribe-stored banners).
   */
  public getDisplayBannerLqipUrl(): string {
    const path = this.pathValue()
    if (path.startsWith('http')) return this.getDisplayBannerSrc()
    const uuid = this._extractUploadUuid(path)
    if (uuid) return `${environment.v2.baseUrl}generic/uploads/${uuid}`
    return this.getDisplayBannerSrc()
  }

  private _resolveFullBannerUrl(source: string): string {
    if (!source) return ''
    if (source.startsWith('http')) return source
    const root = (environment.s3_files_root || '').replace(/\/+$/, '')
    const path = source.replace(/^\/+/, '')
    return root && path ? `${root}/${path}` : root ? `${root}/${source}` : source
  }

  /**
   * Extract upload UUID from a cover_page path or URL (handles lqip and resized suffixes).
   */
  private _extractUploadUuid(source: string): string | null {
    const segment = (source || '').split('/').pop()?.split('?')[0] ?? ''
    const match = segment.match(/^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:-lqip)?(?:-\d+x\d+)?\./i)
    return match?.[1] ?? null
  }

  /**
   * Delete file
   * @returns {void} - The void
   */
  public deleteFile(): void {
    this.toggleModal(this.deleteModalId)
  }

  /**
   * Confirm delete
   */
  public confirmDeleting(): void {
    this.toggleModal(this.deleteModalId)
    const ctrl = this.control()
    // Clear local optimistic/pending state first so UI drops banner immediately.
    this.deletingBanner.set(true)
    this.pendingBannerValue.set(null)
    this.loadedBannerValue.set(null)
    this.controlValue.set('')

    if (ctrl) {
      ctrl.setValue('')
      ctrl.markAsDirty()
      ctrl.markAsTouched()
    }
    this.warningMessage.set('')
    this.fileErrorMessage.set('')
    // Clear stored images and crop box
    this.originalImageFile.set(null)
    this.cropImageFile.set(null)
    this.savedCropBox.set(null)
    // Emit the change when deleting
    this.valueChanged.emit({ field: 'cover_page', value: '' })
  }

  /**
   * Cancel delete – close modal only if still open (avoids double-toggle when X/backdrop already closed it)
   */
  public cancelDelete(): void {
    if (this.isModalOpened(this.deleteModalId)) this.toggleModal(this.deleteModalId)
  }

  /**
   * Toggle modal
   * @param modalId {string} - The modal id
   * @returns {void} - The void
   */
  public toggleModal(modalId: string): void {
    this._ModalService.toggleModal(modalId)
  }

  /**
   * Check if modal is opened
   * @param modalId {string} - The modal id
   * @returns {boolean} - True if modal is opened, false otherwise
   */
  public isModalOpened(modalId: string): boolean {
    return this._ModalService.isModalOpen(modalId)
  }
}
